import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import React, { useContext } from 'react';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { RelationSectionWithPeople } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleSection/RelationSectionWithPeople.tsx';
import { RelatedPersonsSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/RelatedPersonsSection.tsx';
import { PersonRelationType, PersonType } from '@dealstack/shared/lib/generated';
import { EditRelatedPersonDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EditRelatedPersonDrawer/EditRelatedPersonDrawer.tsx';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { PersonDataContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/usePersonData.ts';
import { getRelationLabelByTypeAndPersonType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EditRelatedPersonDrawer/utils/relatedPeopleLabel.ts';
import { RelatedPeopleDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/RelatedPeopleDrawer.tsx';

export const RelatedPeopleSection = ({ hasEditorPermission }: { hasEditorPermission: boolean }) => {
	const tCapManage = useTranslationCapManage();
	const { dealId } = useContext(DealContext);
	const {
		pivs,
		representatives,
		ubos,
		pscs,
		directors,
		trustees,
		settlorsOrGrantors,
		protectorsOrEnforcers,
		beneficiaries,
		otherControllingParties,
		editPersonRelation,
		removePersonRelation,
		editRelation,
		setEditRelation,
		deleteRelation,
		setDeleteRelation,
		deleteRelationLoading,
		deleteRelationSuccess,
		selectedEntityType,
		addPeopleToRelation,
		setAddPeopleToRelation,
	} = useContext(PersonDataContext);

	const naturalPersonRelationSections = [
		{ type: PersonRelationType.REPRESENTATIVE, people: representatives },
		{ type: PersonRelationType.UBO, people: ubos },
		{ type: PersonRelationType.PSC, people: pscs },
		{ type: PersonRelationType.DIRECTOR, people: directors },
		{ type: PersonRelationType.TRUSTEE, people: trustees },
		{ type: PersonRelationType.SETTLER_OR_GRANTOR, people: settlorsOrGrantors },
		{ type: PersonRelationType.PROTECTOR_OR_ENFORCER, people: protectorsOrEnforcers },
		{ type: PersonRelationType.BENEFICIARY, people: beneficiaries },
		{ type: PersonRelationType.OTHER, people: otherControllingParties },
	];

	return (
		<>
			<RelationSectionWithPeople
				title={getRelationLabelByTypeAndPersonType(
					tCapManage,
					PersonRelationType.PIV,
					selectedEntityType
				)}
				people={pivs}
				dealId={dealId}
				relationType={PersonRelationType.PIV}
				onUserDeleteClick={setDeleteRelation}
				onUserEditClick={editPersonRelation}
				onAddRelatedPeople={setAddPeopleToRelation}
				hasEditPermission={hasEditorPermission}
			/>

			{selectedEntityType === PersonType.LEGAL ? (
				<RelatedPersonsSection hasEditorPermission={hasEditorPermission} />
			) : (
				<>
					{naturalPersonRelationSections.map(({ type, people }) => (
						<RelationSectionWithPeople
							key={type}
							title={getRelationLabelByTypeAndPersonType(
								tCapManage,
								type,
								selectedEntityType
							)}
							people={people}
							dealId={dealId}
							relationType={type}
							onUserDeleteClick={setDeleteRelation}
							onUserEditClick={editPersonRelation}
							onAddRelatedPeople={setAddPeopleToRelation}
							hasEditPermission={hasEditorPermission}
						/>
					))}
				</>
			)}

			{hasEditorPermission && (
				<>
					<EditRelatedPersonDrawer
						relatedPerson={editRelation?.relatedPerson}
						relationType={editRelation?.relationType}
						selectedEntityType={selectedEntityType}
						opened={!!editRelation}
						onClose={() => setEditRelation(null)}
					/>

					<RelatedPeopleDrawer
						selectedEntityType={selectedEntityType}
						opened={!!addPeopleToRelation}
						hasPermission={hasEditorPermission}
						onClose={() => setAddPeopleToRelation(null)}
						relationType={addPeopleToRelation}
						relatedPeople={null}
					/>

					<ConfirmDeleteDrawer
						opened={!!deleteRelation}
						onClose={() => setDeleteRelation(null)}
						title={tCapManage('delete.related_person.drawer.title')}
						inputLabel={tCapManage('delete.related_person.drawer.input_label')}
						inputPlaceholder={tCapManage(
							'delete.related_person.drawer.input_placeholder'
						)}
						confirmDeleteText={
							deleteRelation?.relatedPerson?.displayName || 'Related person'
						}
						wordMismatchErrorMessage={tCapManage(
							'delete.related_person.drawer.word_mismatch_error'
						)}
						deleteBtnText={tCapManage('delete.related_person.drawer.confirm_delete')}
						cancelBtnText={tCapManage('delete.related_person.drawer.cancel')}
						handleDelete={removePersonRelation}
						deleteLoading={deleteRelationLoading}
						deleteSuccess={deleteRelationSuccess}
					/>
				</>
			)}
		</>
	);
};
